import { FluentParser, FluentSerializer } from "@fluent/syntax";
import * as Sentry from "@sentry/react";

const Parser = new FluentParser({ withSpans: false });
const Serializer = new FluentSerializer();

const ALLOWED_FTL_NODES = ["Message", "Term"];

/**
 * IMPORTANT FLUENT TERMS
 * ----------------------
 * `message`: an ID/value pair (`try-hotdogs = Try our hot dogs!`)
 * `id`: the message key (`try-hotdogs`)
 * `value`: the message value (`Try our hot dogs!`)
 * `term`: prefixed by "-", a message only readable within the FTL file
 */

/**
 * Returns a list of locale codes used accross all products.
 * @param {object} manifest parsed manifest json
 * @returns {Array} list of all locale codes supported across products
 */
export const getAllSupportedLocales = (manifest) =>
  [...new Set(Object.values(manifest).flatMap((p) => Object.keys(p)))].filter((l) => l !== "en");

/**
 * @param {String} localeCode
 * @param {Object} state current state from reducer
 * @param {Object} manifest object from manifest.json
 * @returns {Boolean} true if the given locale is in the s3 manifest
 */
export const hasSavedLocale = (product, localeCode, manifest) =>
  product in manifest && localeCode in manifest[product];

/**
 * Helper to determine if a locale has been saved and enabled
 * @param {String} localeCode
 * @param {Object} productCode current state from reducer
 * @param {Object} manifest object from manifest.json
 * @returns {Boolean} isEnabled
 */
export const isLocaleEnabled = (localeCode, productCode, manifest) =>
  manifest[productCode]?.[localeCode]?.state === "enabled";

/**
 * Convert an ftl string into an object we can
 * use in `state.locales[locale].messages`
 *
 * @param {String} ftlString file content of an FTL
 * @returns {Object} { messageId: message, messageId: message }
 */
export const ftlToLocaleData = (ftlString) => {
  const errorMessage = "[ftlToLocaleData] failed to serialize messages";
  if (ftlString.length < 1) return {};
  const result = Parser.parse(ftlString)
    .body.filter(({ type }) => ALLOWED_FTL_NODES.includes(type))
    .reduce((messages, message) => {
      const messageId = message.type === "Term" ? `-${message.id.name}` : message.id.name;
      try {
        const serialized = Serializer.serializeEntry(message);
        // eslint-disable-next-line no-param-reassign
        messages[messageId] = serialized.trimEnd();
      } catch (err) {
        Sentry.captureException(errorMessage);
      }
      return messages;
    }, {});

  if (Object.entries(result).length < 1) {
    Sentry.captureException(errorMessage);
  }

  return result;
};

/**
 * Converts a locale (object containing id/msg fluent message entries)
 * to a full FTL file string
 *
 * @param {Object} localeData { '{fluent id}': '{fluent message}', ...}
 * @returns {String} full ftl file string for the given locale
 */
export const localeDataToFtl = (localeData) => Object.values(localeData.messages).join("\n");

/**
 * @param {String} message a single FTL message (full message including ID)
 * @returns {Object} pertinent data from message (validity, id, vars)
 */
export const getMessageData = (message) => {
  let result = {
    isJunk: false,
    id: "",
    vars: [],
  };

  if (!message) {
    return { ...result, isJunk: true };
  }

  const parsed = Parser.parse(message);
  const parsedMessage = parsed.body[0];

  if (!parsedMessage || parsedMessage.type === "Junk") {
    result = { ...result, isJunk: true };
  } else {
    const { id } = parsedMessage;

    // Placeables can appear in the message, function expressions, and selectors.
    // We can look for their references based on the `$` prefix:
    // match `{ $placeable }`, `FUNCTION($placeable)`, `{$placeable}`
    const matches = message.match(new RegExp(/\$[a-zA-Z]{2,}([^\s)}])/gm));
    const vars = matches ? [...new Set(matches)].map((v) => v.replace("$", "")).sort() : [];

    result = {
      ...result,
      id: id.name,
      vars,
    };
  }

  return result;
};

/**
 * Validates a fluent message against its previous value
 * @param {String} originalMsg the message _before_ editing
 * @param {String} newMsg the message _after_ editing
 * @param {String} defaultMsg the value of the message in our default FTL
 * @returns {Object} validity and reason message
 */
export const validateMessage = (originalMsg, newMsg, defaultMsg) => {
  let result = { isValid: true };
  const originalData = getMessageData(originalMsg);
  const newData = getMessageData(newMsg);
  const newVars = [...new Set(newData.vars)];
  const defaultData = getMessageData(defaultMsg);
  const defaultVars = [...new Set(defaultData.vars)];

  // users can remove vars and reuse vars within a message - BUT -
  // They can NOT add vars that do not appear in our default FTL for this message
  const isVarChangeValid = newVars.every((v) => defaultVars.includes(v));

  if (newData.isJunk) {
    result = {
      isValid: false,
      reason: "Invalid Fluent syntax",
    };
  }

  if (!newData.isJunk && !isVarChangeValid) {
    result = {
      isValid: false,
      reason: `${newData.id} has an invalid variable change.
      \n\nValid variables: ${defaultVars.join(",")}
      \n\nReceived: ${newVars.join(",")}`,
    };
  }

  if (!newData.isJunk && originalData.id !== newData.id) {
    result = {
      isValid: false,
      reason: "ID does not match original. Please do not modify message IDs.",
    };
  }

  return result;
};

/**
 * Validate an entire FTL file string. Useful for raw editing.
 *
 * @param {String} originalFtl
 * @param {String} newFtl
 * @param {String} defaultFtl
 * @param {Object} options - { allowNewMessages: boolean }
 *   - allowNewMessages: when true, allows adding messages not in original or default FTL.
 *     New messages are still validated for syntax but not for variable constraints.
 * @returns {Object} validity and reason message
 */
export const validateFtl = (
  originalFtl,
  newFtl,
  defaultFtl,
  opts = {
    allowNewMessages: false,
  }
) => {
  let result = { isValid: true };

  // Immediately fail on invalid syntax
  if (Parser.parse(newFtl).body.some(({ type }) => type === "Junk")) {
    return {
      isValid: false,
      reason: "Modified FTL contains invalid syntax",
    };
  }

  const originalLocaleData = ftlToLocaleData(originalFtl);
  const newLocaleData = ftlToLocaleData(newFtl);
  const defaultLocaleData = ftlToLocaleData(defaultFtl);

  // Validate every message; fail validation on first error encountered
  Object.entries(newLocaleData).every(([id, message]) => {
    const isInOriginal = originalLocaleData[id] !== undefined;
    const isInDefault = defaultLocaleData[id] !== undefined;
    const isNewMessage = !isInOriginal && !isInDefault;

    // Handle new messages (not in original or default)
    if (isNewMessage) {
      if (!opts.allowNewMessages) {
        result = {
          isValid: false,
          reason: `ID ${id} is not defined in the default FTL.`,
        };
        return false;
      }
      return true;
    }

    // Handle restored messages (in default but not in original)
    if (!isInOriginal && isInDefault) {
      return true;
    }

    // Stale message: present in the translation but absent from the default
    // (no callers) — nothing interpolates its vars, so don't block the save.
    if (isInOriginal && !isInDefault) {
      return true;
    }

    // Validate message for var and id integrity
    const { isValid, reason } = validateMessage(
      originalLocaleData[id],
      message,
      defaultLocaleData[id]
    );
    if (!isValid) {
      result = { isValid, reason };
      return false;
    }
    return true;
  });

  return result;
};

// Include typescript utilities
export * from "./id";
